import { TimelineBenefitEvent, TimelineItemType } from '@/components/Timeline/types';
import { localIsoDate } from '@bariendo/lib/benefit-events';
import { CoverageAction } from '@bariendo/lib/constants';
import { COVERAGE_ACTION_DOCUMENT_EXTENSION } from '@bariendo/lib/extensions';
import { prepDoneAt, readCoverageAction } from '@bariendo/lib/payment-status';
import { prepTaskCode, readPrepItems } from '@bariendo/lib/prior-auth-prep';
import { createReference, getReferenceString } from '@medplum/core';
import { Claim, ClaimResponse, DocumentReference, Practitioner, Reference, Task } from '@medplum/fhirtypes';
import { filterShowsBilling } from './billingFilter';

/** Each step's card title, in the spec's words (`timeline-employer-card.mdx`, `timeline-insurance-card.mdx`). */
export function coverageCardTitle(action: CoverageAction, payorName: string, insurance: boolean): string {
  switch (action) {
    case CoverageAction.REFERRAL_SENT:
      return `Referral sent to ${payorName}`;
    case CoverageAction.BENEFIT_CONFIRMED:
      return `Benefit confirmed by ${payorName}`;
    case CoverageAction.CASE_FORM_SUBMITTED:
      return `Case form submitted to ${payorName}`;
    case CoverageAction.APPROVAL_DENIED:
      return `Approval denied by ${payorName}`;
    case CoverageAction.BENEFIT_VERIFIED:
      return `Benefit verified with ${payorName}`;
    case CoverageAction.QUESTIONNAIRE_RECEIVED:
      return 'Questionnaire received';
    case CoverageAction.PSYCH_EVALUATION_RECEIVED:
      return 'Psych evaluation received';
    // Retired (BAR-3128); an earlier step still shows on the timeline.
    case CoverageAction.PCP_CLEARANCE_RECEIVED:
      return 'PCP clearance received';
    case CoverageAction.PRIOR_AUTH_SUBMITTED:
      return `Prior auth submitted to ${payorName}`;
    case CoverageAction.PRIOR_AUTH_DENIED:
      return `Prior auth denied by ${payorName}`;
    case CoverageAction.NO_BENEFIT:
      return `No benefit with ${payorName}`;
    case CoverageAction.APPROVED:
      return insurance ? `Prior auth approved by ${payorName}` : `Approved by ${payorName}`;
  }
}

/**
 * Maps coverage steps (BAR-2593, BAR-3078) to timeline cards, one per Claim or ClaimResponse:
 * the record is append-only, so cards never coalesce or rewrite. `insurance` picks the
 * insurance wording where the two tracks differ.
 */
export function mapBenefitEventsToTimelineItems(
  resources: (Claim | ClaimResponse)[],
  options: { insurance?: boolean; prepTasks?: Task[] } = {}
): TimelineBenefitEvent[] {
  const items: TimelineBenefitEvent[] = [];
  for (const resource of resources) {
    const action = readCoverageAction(resource);
    if (!resource.id || !resource.created || !action) {
      continue;
    }
    const actionDate = resource.created.slice(0, 10);
    const recordedAt = resource.meta?.lastUpdated ? new Date(resource.meta.lastUpdated) : undefined;
    const payorName = resource.insurer?.display ?? 'the payor';
    items.push({
      reference: createReference(resource) as Reference<Claim | ClaimResponse>,
      type: TimelineItemType.BENEFIT_EVENT,
      id: resource.id,
      action,
      title: coverageCardTitle(action, payorName, options.insurance ?? false),
      payorName,
      actionDate,
      documents: (resource.extension ?? [])
        .filter((e) => e.url === COVERAGE_ACTION_DOCUMENT_EXTENSION && e.valueReference)
        .map((e) => e.valueReference as Reference<DocumentReference>),
      note:
        resource.resourceType === 'Claim' ? resource.supportingInfo?.[0]?.valueString : resource.processNote?.[0]?.text,
      recordedBy: resource.meta?.author,
      recordedByName: resource.meta?.author?.display,
      recordedAt,
      timestamp: positionFor(actionDate, recordedAt),
    });
  }
  const ready = priorAuthReadyItem(options.prepTasks ?? []);
  return ready ? [...items, ready] : items;
}

export const PRIOR_AUTH_READY_TITLE = 'Prior auth ready · all four prep items done';

/**
 * The system card when the newest prep set is all done or waived (BAR-3089), at the last item's
 * date. Derived, like the payment cards, so there is one per set and nothing to write.
 */
export function priorAuthReadyItem(prepTasks: Task[]): TimelineBenefitEvent | undefined {
  const doneAt = prepDoneAt(readPrepItems(prepTasks));
  const newest = [...prepTasks]
    .filter((task) => prepTaskCode(task))
    .sort((a, b) => (b.authoredOn ?? '').localeCompare(a.authoredOn ?? ''))[0];
  const focus = newest?.focus?.reference;
  if (!doneAt || !focus) {
    return undefined;
  }
  const actionDate = doneAt.slice(0, 10);
  return {
    reference: { reference: focus } as Reference<Claim>,
    type: TimelineItemType.BENEFIT_EVENT,
    id: `prior-auth-ready-${focus}`,
    title: PRIOR_AUTH_READY_TITLE,
    payorName: '',
    actionDate,
    documents: [],
    timestamp: positionFor(actionDate, doneAt.length > 10 ? new Date(doneAt) : undefined),
  };
}

function positionFor(actionDate: string, recordedAt: Date | undefined): Date {
  if (recordedAt && localIsoDate(recordedAt) === actionDate) {
    return recordedAt;
  }
  return new Date(`${actionDate}T12:00:00.000Z`);
}

export function filterBenefitEventItems(
  items: TimelineBenefitEvent[],
  activeFilter: string,
  provider?: Reference<Practitioner> | null,
  startDate?: Date | null,
  endDate?: Date | null
): TimelineBenefitEvent[] {
  if (!filterShowsBilling(activeFilter)) {
    return [];
  }
  let filtered = items;
  if (startDate) {
    const start = new Date(startDate);
    start.setHours(0, 0, 0, 0);
    filtered = filtered.filter((item) => item.timestamp >= start);
  }
  if (endDate) {
    const end = new Date(endDate);
    end.setHours(23, 59, 59, 999);
    filtered = filtered.filter((item) => item.timestamp <= end);
  }
  if (provider) {
    const wanted = getReferenceString(provider);
    filtered = filtered.filter((item) => (item.recordedBy ? getReferenceString(item.recordedBy) === wanted : false));
  }
  return filtered;
}
